<template>
  <transition name="menu" mode="out-in">
    <div
      class="fullscreen-menu"
      v-if="isOpen"
      role="dialog"
      aria-modal="true"
      aria-labelledby="menu-heading"
    >
      <div class="menu-scroll-wrapper">
        <div class="menu-content">
          <GridContainer>
            <GridParent>
              <nav class="">
                <ul :style="{ '--stagger-count': menuItems.length }">
                  <li
                    v-for="(item, index) in menuItems"
                    :key="index"
                    class="animate rise"
                    :style="{ '--stagger-index': index }"
                  >
                    <a
                      v-if="item.external"
                      :href="item.route"
                      @click.prevent="handleMenuClick(item.route, true)"
                    >
                      <DynamicText class="text" as="h1" :attrs="{ class: '' }" :text="item.text" />
                    </a>
                    <router-link
                      v-else
                      :to="item.route"
                      active-class="is-active"
                      exact-active-class="is-active"
                      @click.prevent="handleMenuClick(item.route)"
                    >
                      <DynamicText class="text" as="h1" :attrs="{ class: '' }" :text="item.text" />
                    </router-link>
                  </li>
                </ul>
              </nav>
            </GridParent>
          </GridContainer>
        </div>
      </div>
      <div v-if="isMenuActive" class="menu-overlay"></div>
    </div>
  </transition>
</template>

<script>
import GridContainer from './grid/GridContainer.vue';

export default {
  props: {
    isOpen: Boolean,
  },
  data() {
    return {
      isMenuActive: false, // Initially hidden
      scrollPosition: 0,
      menuItems: [
        { text: 'Home', route: '/' },
        { text: 'Library', route: '/library' },
        { text: 'About', route: '/about' },
        { text: 'Resume', route: '/resume.html', external: true },
        { text: 'Design System', route: '/storybook/', external: true },
        { text: 'Contact', route: 'mailto:jacques@ramphal.design', external: true },
      ],
    };
  },
  methods: {
    closeMenu() {
      this.$emit('close');
    },
    handleEscapeKey(event) {
      if (event.key === 'Escape') {
        this.closeMenu();
      }
    },
    toggleMenu() {
      this.isMenuActive = !this.isMenuActive;
    },
    handleMenuClick(route, isExternal = false) {
      // Tells the page transition this navigation started in the menu.
      if (!isExternal) document.documentElement.classList.add('vt-from-menu');
      if (isExternal) {
        // For external/static links, use direct navigation
        window.location.href = route;
      } else {
        // Navigate first, then close menu after route changes
        this.$router.push(route).then(() => {
          // Same page, so no transition ran to clear the flag.
          const root = document.documentElement;
          if (!root.classList.contains('vt-active')) root.classList.remove('vt-from-menu');
          this.$nextTick(() => {
            requestAnimationFrame(() => {
              this.closeMenu();
            });
          });
        });
      }
    },
  },
  // Register the event listener when the component is mounted
  mounted() {
    window.addEventListener('keydown', this.handleEscapeKey);
  },
  // Remove the event listener when the component is destroyed
  beforeUnmount() {
    window.removeEventListener('keydown', this.handleEscapeKey);

    // Clean up body scroll lock if menu is still open. Restore the old scroll
    // position only if we're still on the page it came from; a link in the
    // menu lands at the top of the new page.
    if (this.isOpen) {
      const scrollY = this.$route.fullPath === this.openedOn ? this.scrollPosition : 0;
      document.documentElement.classList.remove('menu-open');
      document.body.classList.remove('menu-open');
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflowY = '';
      window.scrollTo(0, scrollY);
    }
  },
  watch: {
    isOpen: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          // Save current scroll position, and the page it belongs to
          this.scrollPosition = window.scrollY;
          this.openedOn = this.$route.fullPath;

          // Lock body scroll
          document.documentElement.classList.add('menu-open');
          document.body.classList.add('menu-open');
          document.body.style.position = 'fixed';
          document.body.style.top = `-${this.scrollPosition}px`;
          document.body.style.width = '100%';
          document.body.style.overflowY = 'scroll'; // Keep scrollbar space
        } else {
          // Unlock body scroll
          const scrollY = this.scrollPosition;
          document.documentElement.classList.remove('menu-open');
          document.body.classList.remove('menu-open');
          // Restore scroll synchronously before paint to avoid 1-frame jump
          document.documentElement.scrollTop = scrollY;
          document.body.scrollTop = scrollY;
          document.body.style.position = '';
          document.body.style.top = '';
          document.body.style.width = '';
          document.body.style.overflowY = '';
        }
      },
    },
  },
  components: {
    // MainFooter,
    GridContainer,
  },
};
</script>

<style lang="scss">
// Prevent scrolling on the page when menu is open
html.menu-open,
body.menu-open {
  overflow: hidden !important;
  touch-action: none !important;
  -webkit-overflow-scrolling: auto !important;
}

.menu-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: var(--z-overlay);
}
.text {
  font-weight: var(--fontWeight-bold) !important;
}
/* Menu items are h1s, which now sit at --lineHeight-shortest (1). A list of
   them needs more air than a single title. */
.menu-content nav li {
  padding-block: var(--spacing-xxs);
}
.menu-content nav h1 {
  line-height: var(--lineHeight-short);
}
.fullscreen-menu {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  height: 100vh;
  background-color: var(--background);
  box-shadow: var(--shadow-heavy);
  z-index: var(--z-menu);
  border-block-end: var(--border);
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  transition: none !important;
  transform: none !important;

  @media only screen and (min-width: 1201px) {
    inline-size: 100vw;
  }

  .close-button {
    position: absolute;
    inset-inline-end: var(--spacing-xs);
    inset-block-start: var(--spacing-xs);
  }

  &::after {
    content: '';
    position: absolute;
    inset-block-start: 0;
    inset-block-end: 0;
    inset-inline-end: -100%;
    inline-size: 100%;
    background-color: var(--background);
    z-index: -1;
    transition: none;
  }
}

// Wrapper that enables proper scrolling
.menu-scroll-wrapper {
  min-height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: end;
}

// Content container
.menu-content {
  width: 100%;

  nav {
    grid-column: span 3;

    // The list sits on the bottom edge (.menu-scroll-wrapper justifies end).
    padding-block-end: var(--spacing-md);

    ul {
      list-style: none;
      padding: 0;
      margin: 0;

      li {
        a {
          text-decoration: none;
          color: var(--foreground) !important;

          // The site's wavy hover, scaled to the item size. The global a:hover
          // sizes it in rem for body text, which reads as a thread under an h1.
          &:hover {
            text-decoration: none !important;
          }
          &:hover h1 {
            text-decoration: underline wavy var(--accent-line);
            text-decoration-thickness: 0.05em;
            text-underline-offset: 0.14em;
          }

          &.is-active h1::before {
            content: '~ ';
          }
        }
      }
    }
  }
}

// The menu's own transition (App.vue wraps it in <transition name="menu">).
// Named "menu" because this block is unscoped: as "fade" it overrode the page
// transition. Items arrive with .animate.rise; on close they sink in reverse,
// last item first, then the panel fades. Animations, not transitions, because
// .fullscreen-menu sets transition: none.
.fullscreen-menu.menu-leave-active {
  animation: animate-fade-out var(--duration-fast) var(--ease-exit) calc(var(--duration-fast) + 20ms) both;
}

.fullscreen-menu.menu-leave-active li.animate.rise {
  animation: animate-sink var(--duration-fast) var(--ease-exit) both;
  animation-delay: calc((var(--stagger-count, 1) - 1 - var(--stagger-index, 0)) * 30ms);
}

@media (prefers-reduced-motion: reduce) {
  .fullscreen-menu.menu-leave-active,
  .fullscreen-menu.menu-leave-active li.animate.rise {
    animation: none;
  }
}
</style>
